import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Circle, Trophy, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';

/**
 * Dashboard "Setup score" widget (iter371).
 *
 * Shows a circular progress ring with the tenant's onboarding
 * completion (0-100), plus a per-task list with green ticks for
 * done items and ghosted circles + deep-link arrows for pending
 * items. Hides itself once the score hits 100 so power-users
 * don't keep seeing a permanent "100% complete" banner.
 */
export default function DashboardSetupScore() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/admin/setup-score').then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || data.score >= 100) return null;

  // Adapt the existing /admin/setup-score response shape (`completed[]`
  // + `incomplete[]` arrays) into a unified items[] list for rendering.
  const score = data.score;
  const completed = data.completed_count ?? (data.completed?.length || 0);
  const total = data.total_signals ?? ((data.completed?.length || 0) + (data.incomplete?.length || 0));
  const items = [
    ...(score >= 80 ? [] : (data.completed || []).map(t => ({ ...t, done: true }))),
    ...(data.incomplete || []).map(t => ({ ...t, done: false })),
  ];
  // Render a SVG circle with stroke-dashoffset for the progress ring.
  const radius = 38;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;

  return (
    <Card
      className="p-5 bg-gradient-to-br from-emerald-50 via-white to-violet-50 border-emerald-200/70"
      data-testid="dashboard-setup-score"
    >
      <div className="flex items-start gap-5">
        {/* Progress ring */}
        <div className="relative shrink-0" data-testid="setup-score-ring">
          <svg width="96" height="96" viewBox="0 0 96 96">
            <circle cx="48" cy="48" r={radius} stroke="#e2e8f0" strokeWidth="8" fill="none" />
            <circle
              cx="48" cy="48" r={radius}
              stroke="url(#setup-grad)" strokeWidth="8" fill="none" strokeLinecap="round"
              strokeDasharray={`${dash} ${circ}`}
              transform="rotate(-90 48 48)"
              style={{ transition: 'stroke-dasharray 600ms ease-out' }}
            />
            <defs>
              <linearGradient id="setup-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#7c3aed" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold text-foreground" data-testid="setup-score-value">{score}</span>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Setup</span>
          </div>
        </div>

        {/* Task list */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Trophy size={16} className="text-amber-500" />
            <h3 className="font-outfit font-semibold text-base">Setup score</h3>
            <span className="text-xs text-muted-foreground">
              {completed} of {total} done — keep going!
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-3 max-w-md">
            Every tile lit unlocks more of Smarto&apos;s power. Knock these out in
            any order — most take under 30 seconds.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {items.map(t => (
              <div
                key={t.key}
                className={`group flex items-center justify-between rounded-lg px-2.5 py-1.5 transition-colors ${
                  t.done ? 'bg-emerald-50/60' : 'bg-white border border-dashed border-border hover:border-violet-300 hover:bg-violet-50/40'
                }`}
                data-testid={`setup-task-${t.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {t.done ? (
                    <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                  ) : (
                    <Circle size={14} className="text-muted-foreground shrink-0" />
                  )}
                  <span className={`text-xs truncate ${t.done ? 'text-emerald-800' : 'text-foreground'}`}>{t.label}</span>
                </div>
                {!t.done && (
                  <Link
                    to={t.path || t.route || '#'}
                    className="text-violet-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`setup-task-go-${t.key}`}
                    aria-label={`Go to ${t.label}`}
                  >
                    <ArrowRight size={13} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
